import { FC, RefObject, useEffect, useState } from "react";
import { Button, Loader } from "@mantine/core";
import { TeamPlayerModal } from "./TeamPlayerModal";
import {
  gameScoreGoal,
  gameCorrectGoal,
  gameRevokeGoal,
} from "../../../../openapi/api-client";
import { useMutation } from "@tanstack/react-query";
import { v4 as uuid } from "uuid";
import { TeamName } from "../../../../Components/TeamName";
import { useGame } from "../../../../hooks/useGame.ts";
import { useCompetition } from "../../../../hooks/useCompetition.ts";

interface GoalButtonProps {
  teamId: string;
  disabled: boolean;
  time: number;
  gameId: string;
  goals: number;
  skipScorerModal?: boolean;
  triggerRef?: RefObject<(() => void) | undefined>;
}

export const GoalButton: FC<GoalButtonProps> = (props) => {
  const { teamId, disabled, time, gameId, goals, skipScorerModal, triggerRef } =
    props;
  const [modalOpened, setModalOpened] = useState(false);

  // State for the two-step flow (scorer -> assist)
  const [currentGoalId, setCurrentGoalId] = useState<string | undefined>();
  const [scorer, setScorer] = useState<string | undefined>();
  const [isOwnGoal, setIsOwnGoal] = useState<boolean>(false);

  const scoreGoalMutation = useMutation({ mutationFn: gameScoreGoal });
  const correctGoalMutation = useMutation({ mutationFn: gameCorrectGoal });
  const revokeGoalMutation = useMutation({ mutationFn: gameRevokeGoal });

  const game = useGame(gameId);
  const competition = useCompetition(game.data?.competitionId);

  const isReady =
    game.isSuccess &&
    game.data.actualTeams.homeTeamId &&
    game.data.actualTeams.guestTeamId &&
    competition.isSuccess;

  const homeTeam = isReady
    ? game.data.actualTeams.homeTeamId === teamId
    : false;

  const otherTeamId = isReady
    ? homeTeam
      ? game.data.actualTeams.guestTeamId
      : game.data.actualTeams.homeTeamId
    : undefined;

  const resetState = () => {
    setModalOpened(false);
    setCurrentGoalId(undefined);
    setScorer(undefined);
    setIsOwnGoal(false);
  };

  const onGoalButtonClick = async () => {
    if (!isReady || disabled || scoreGoalMutation.isPending) return;

    const goalId = uuid();

    // Immediately record the goal without scorer
    await scoreGoalMutation.mutateAsync({
      id: gameId,
      time: time,
      goalId: goalId,
      homeTeam,
      isOwnGoal: false,
    });

    // Open modal for scorer selection unless skipped
    if (!skipScorerModal) {
      setCurrentGoalId(goalId);
      setModalOpened(true);
    }
  };

  // Expose trigger function to parent via ref
  useEffect(() => {
    if (triggerRef) {
      triggerRef.current = () => void onGoalButtonClick();
    }
    return () => {
      if (triggerRef) {
        triggerRef.current = undefined;
      }
    };
  });

  if (!isReady) {
    return <Loader />;
  }

  const assignScorer = (playerId: string, assistPlayerId?: string) => {
    if (!currentGoalId) return;

    void correctGoalMutation
      .mutateAsync({
        id: gameId,
        goalId: currentGoalId,
        playerId,
        assistPlayerId,
        isOwnGoal,
      })
      .then(() => {
        resetState();
      });
  };

  const onPlayerClick = (playerId?: string) => {
    // If assists are not relevant, assign scorer immediately
    if (!competition.data.settings.topscorerCountAssists) {
      if (playerId == undefined) {
        throw new Error("playerId not set");
      }
      assignScorer(playerId);
      return;
    }

    // Assists are relevant. First click sets the scorer, second click sets the assist
    if (!scorer) {
      if (playerId == undefined) {
        throw new Error("playerId not set");
      }
      setScorer(playerId);
      return;
    }

    // Scorer is already set, this is the assist player (can be undefined for "keine Vorlage")
    assignScorer(scorer, playerId);
  };

  const onNotSeen = () => {
    // Just close the modal - goal is already recorded without scorer
    resetState();
  };

  const onCancel = () => {
    // Delete the goal and close the modal
    if (currentGoalId) {
      void revokeGoalMutation.mutateAsync({
        id: gameId,
        goalId: currentGoalId,
      });
    }
    resetState();
  };

  return (
    <>
      <TeamPlayerModal
        teamId={scorer ? teamId : isOwnGoal ? otherTeamId! : teamId}
        opened={modalOpened}
        close={onCancel}
        onClick={onPlayerClick}
        showEmptyPlayer={scorer ? true : !isOwnGoal}
        emptyPlayerText={
          scorer ? (
            <>
              keine
              <br />
              Vorlage
            </>
          ) : (
            <>Eigentor</>
          )
        }
        emptyPlayerAction={
          scorer
            ? undefined
            : () => {
                setIsOwnGoal(true);
              }
        }
        secondaryEmptyPlayerText={
          !scorer ? (
            <>
              nicht
              <br />
              gesehen
            </>
          ) : undefined
        }
        secondaryEmptyPlayerAction={!scorer ? onNotSeen : undefined}
        buttonColor={scorer ? "green" : "blue"}
        disabledPlayerIds={scorer ? [scorer] : undefined}
        allDisabled={
          scoreGoalMutation.isPending || correctGoalMutation.isPending
        }
        headline={
          <>
            {scorer ? <>Vorlage</> : <>Torschütze</>} für{" "}
            <TeamName teamId={teamId} />
          </>
        }
      />
      <Button
        size="xl"
        fullWidth
        color="blue"
        disabled={disabled || scoreGoalMutation.isPending}
        onClick={() => void onGoalButtonClick()}
        styles={() => ({
          root: { height: 150, fontSize: 60 },
        })}
      >
        {goals}
      </Button>
    </>
  );
};
